import {
  EnrichmentFieldState,
  type EnrichmentRunSummary,
  type EnrichmentStatusEntry,
  type EnrichmentStatusResponse,
  type RefreshForUnlockResponse,
  type RefreshOutcomeDto,
} from '@factoryfixinc/ats-interfaces';
import { defineStore } from 'pinia';
import { computed, ref } from 'vue';

import EnrichmentStatusPersistence from '@/core/sourcing/enrichment-status/enrichment-status.persistence';
import SelectedCandidateService from '@/core/sourcing/selected-candidate/selected-candidate.service';

const POLL_INTERVAL_MS = 1000;
const POLL_JITTER_MS = 200;
const MAX_POLLING_DURATION_MS = 30_000;
const MAX_CONSECUTIVE_FAILURES = 2;

// Session-scoped: outcomes are not persisted server-side, so the chip does
// not re-render on return visits.
export const useProUnlockStore = defineStore('sourcing-pro-unlock', () => {
  const outcomes = ref<Record<number, RefreshOutcomeDto | null>>({});
  const fieldStates = ref<Record<number, EnrichmentStatusEntry['fields']>>({});
  const loadingIds = ref<Record<number, true>>({});
  const results = ref<Record<number, EnrichmentRunSummary | null>>({});
  const pendingStatusIds = new Set<number>();
  const refetchedIds = new Set<number>();
  const enrichmentStatusPersistence = new EnrichmentStatusPersistence();
  let capTimeoutId: number | null = null;
  let pollingTimeoutId: number | null = null;
  let pollingStartedAt: number | null = null;
  let consecutiveFailures = 0;
  let pollingGeneration = 0;

  function markLoading(userProfileIds: number[]): void {
    userProfileIds.forEach((id) => {
      loadingIds.value[id] = true;
    });
  }

  function clearLoading(userProfileIds: number[]): void {
    userProfileIds.forEach((id) => {
      delete loadingIds.value[id];
    });
  }

  function setOutcomes(
    unlockedProfileIds: number[],
    enrichmentResults: RefreshForUnlockResponse,
  ): void {
    unlockedProfileIds.forEach((id) => {
      outcomes.value[id] = enrichmentResults[id] ?? null;
      delete loadingIds.value[id];
    });
  }

  function setStatuses(statuses: EnrichmentStatusResponse): void {
    Object.entries(statuses).forEach(([rawId, status]) => {
      const id = Number(rawId);

      fieldStates.value[id] = status.fields;
      results.value[id] = status.result ?? null;
      if (status.outcome) {
        outcomes.value[id] = status.outcome;
      }
    });
  }

  function degradeOutcomes(userProfileIds: number[], at: string): void {
    userProfileIds.forEach((id) => {
      outcomes.value[id] = { outcome: 'checked', reason: 'degraded', at };
    });
  }

  function startEnrichmentStatusPolling(userProfileIds: number[]): void {
    userProfileIds.forEach((id) => {
      if (outcomeFor.value(id)?.outcome !== 'checking') return;

      if (!pendingStatusIds.has(id)) {
        refetchedIds.delete(id);
      }
      pendingStatusIds.add(id);
    });

    if (!pendingStatusIds.size || pollingStartedAt !== null) return;

    pollingStartedAt = Date.now();
    pollingGeneration += 1;
    const generation = pollingGeneration;
    // The watchdog caps the whole active loop. IDs merged later intentionally
    // inherit its remaining time instead of extending polling past 30 seconds.
    capTimeoutId = window.setTimeout(() => {
      if (pollingGeneration === generation) {
        degradePendingIds();
      }
    }, MAX_POLLING_DURATION_MS);
    scheduleNextPoll();
  }

  function stopEnrichmentStatusPolling(): void {
    if (capTimeoutId !== null) {
      window.clearTimeout(capTimeoutId);
    }
    if (pollingTimeoutId !== null) {
      window.clearTimeout(pollingTimeoutId);
    }

    capTimeoutId = null;
    pollingTimeoutId = null;
    pollingStartedAt = null;
    consecutiveFailures = 0;
    pollingGeneration += 1;
    pendingStatusIds.clear();
    refetchedIds.clear();
  }

  function scheduleNextPoll(): void {
    if (pollingStartedAt === null || !pendingStatusIds.size) {
      stopEnrichmentStatusPolling();
      return;
    }

    const remainingMs = MAX_POLLING_DURATION_MS - (Date.now() - pollingStartedAt);
    if (remainingMs <= 0) {
      degradePendingIds();
      return;
    }

    const jitterMs = (Math.random() * 2 - 1) * POLL_JITTER_MS;
    const delayMs = Math.min(POLL_INTERVAL_MS + jitterMs, remainingMs);
    const generation = pollingGeneration;
    pollingTimeoutId = window.setTimeout(() => void pollEnrichmentStatuses(generation), delayMs);
  }

  async function pollEnrichmentStatuses(generation: number): Promise<void> {
    pollingTimeoutId = null;

    if (pollingStartedAt === null || pollingGeneration !== generation) return;
    if (Date.now() - pollingStartedAt >= MAX_POLLING_DURATION_MS) {
      degradePendingIds();
      return;
    }

    const ids = [...pendingStatusIds].filter((id) => {
      const isChecking = outcomeFor.value(id)?.outcome === 'checking';
      if (!isChecking) pendingStatusIds.delete(id);
      return isChecking;
    });

    if (!ids.length) {
      stopEnrichmentStatusPolling();
      return;
    }

    try {
      const statuses = await enrichmentStatusPersistence.fetchStatuses(ids);
      if (pollingGeneration !== generation) return;

      consecutiveFailures = 0;
      setStatuses(statuses);

      ids.forEach((id) => {
        const status = statuses[id];
        if (!status) return;

        refetchCandidateWhenUpdated(id, status);
        if (status.outcome) {
          pendingStatusIds.delete(id);
        }
      });
    } catch {
      if (pollingGeneration !== generation) return;

      consecutiveFailures += 1;
      if (consecutiveFailures >= MAX_CONSECUTIVE_FAILURES) {
        degradePendingIds();
        return;
      }
    }

    scheduleNextPoll();
  }

  function refetchCandidateWhenUpdated(id: number, status: EnrichmentStatusEntry): void {
    if (refetchedIds.has(id)) return;
    if (!Object.values(status.fields).includes(EnrichmentFieldState.UPDATED)) return;

    const selectedCandidateService = new SelectedCandidateService();
    if (selectedCandidateService.candidate?.id !== id) return;

    refetchedIds.add(id);
    selectedCandidateService.fetchCandidate(id).catch(() => undefined);
  }

  function degradePendingIds(): void {
    degradeOutcomes([...pendingStatusIds], new Date().toISOString());
    stopEnrichmentStatusPolling();
  }

  const outcomeFor = computed(
    () =>
      (userProfileId: number): RefreshOutcomeDto | null =>
        outcomes.value[userProfileId] ?? null,
  );

  const isLoading = computed(
    () =>
      (userProfileId: number): boolean =>
        !!loadingIds.value[userProfileId],
  );

  return {
    outcomes,
    fieldStates,
    loadingIds,
    results,
    markLoading,
    clearLoading,
    setOutcomes,
    setStatuses,
    degradeOutcomes,
    startEnrichmentStatusPolling,
    stopEnrichmentStatusPolling,
    outcomeFor,
    isLoading,
  };
});
